// ============================================================================
// FOLIO TYPES (PMS Phase 2 slice 5)
// ============================================================================
// The In-House Guests list and the folio screen. Matches
// crs.Core.DTOs.FolioModule.FolioDeskDtos and FolioPostingResult.

/** Actions of the In-House Guests screen (module `pms-inhouse`). */
export const INHOUSE_ACTIONS = {
  view: 'view',
  postCharge: 'post-charge',
  reverseCharge: 'reverse-charge',
  receivePayment: 'receive-payment',
  postingControl: 'posting-control',
  export: 'export',
  // Folio Charge Vouchers (slice 7c): beyond post-charge, by the charge type's nature.
  postRecovery: 'post-recovery',
  postDisbursement: 'post-disbursement',
  postLateCharge: 'post-late-charge',
  waiveRetention: 'waive-retention',
  // Buffet check-off slice 1 (fixes/087): change a stay's MAP meal preference, for the stay or a day.
  meals: 'meals',
} as const;

/** pms-inhouse in the role matrix. */
export const INHOUSE_MODULE = 'pms-inhouse';

/** Who paid a disbursement's supplier. */
export const PAID_OUT_OPTIONS = [
  { value: 'DESK_CASH', label: 'Cash paid out at the desk' },
  { value: 'ACCOUNTS', label: 'Paid by Accounts' },
] as const;

export interface InHouseFolioQuery {
  propertyId: string;
  search?: string;
  attentionOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface InHouseFolioRow {
  folioId: string;
  folioNumber: string;
  bookingId: string;
  bookingNumber: string;
  checkInId?: string | null;
  grcNumber?: string | null;
  guestFullName?: string | null;
  guestPhone?: string | null;
  isVip: boolean;
  partyName?: string | null;
  bookingSource?: string | null;
  settlementMode?: string | null;
  roomNumbers?: string | null;
  roomCount: number;
  mealPlanSummary?: string | null;
  adults: number;
  children: number;
  nationality?: string | null;
  checkedInAt?: string | null;
  expectedCheckOut: string;
  totalCharges: number;
  totalPayments: number;
  /** Charges − payments. Positive = the guest owes; negative = credit held. */
  currentBalance: number;
  partyCharges: number;
  noPost: boolean;
  creditLimit?: number | null;
}

export type FolioLineKind = 'CHARGE' | 'PAYMENT';

export interface FolioLine {
  kind: FolioLineKind;
  id: string;
  lineDate: string;
  createdAt?: string | null;
  description: string;
  chargeCategory?: string | null;
  chargeName?: string | null;
  sourceModule?: string | null;
  sourceRef?: string | null;
  outletName?: string | null;
  invoicedElsewhereRef?: string | null;
  billToType?: string | null;
  billToName?: string | null;
  billingState?: string | null;
  quantity: number;
  unitPrice: number;
  amount: number;
  taxAmount: number;
  taxSummary?: string | null;
  reversed: boolean;
  reversalOfId?: string | null;
  reversalReason?: string | null;
  postedByName?: string | null;
  paymentType?: string | null;
  paymentMode?: string | null;
  paymentReference?: string | null;
  paymentId?: string | null;
  receiptNumber?: string | null;
  remarks?: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
  canReverse: boolean;
  /** The Folio Charge Voucher that posted the line (slice 7c). */
  voucherId?: string | null;
  voucherNumber?: string | null;
  chargeNature?: string | null;
  /** A disbursement at cost — a reimbursement on the invoice. */
  isReimbursement?: boolean;
  /** On an issued invoice: corrected by credit note, not reversed. */
  invoiceId?: string | null;
}

export interface FolioDetail {
  folioId: string;
  folioNumber: string;
  folioStatusCode?: string | null;
  folioStatusName?: string | null;
  isOpen: boolean;
  bookingId: string;
  bookingNumber: string;
  checkInId?: string | null;
  grcNumber?: string | null;
  propertyId: string;
  propertyName?: string | null;
  guestFullName?: string | null;
  partyName?: string | null;
  settlementMode?: string | null;
  roomNumbers?: string | null;
  mealPlanSummary?: string | null;
  adults: number;
  children: number;
  openedOn?: string | null;
  expectedCheckOut: string;
  closedAt?: string | null;
  businessDate: string;
  totalCharges: number;
  totalPayments: number;
  currentBalance: number;
  guestCharges: number;
  partyCharges: number;
  guestBalance: number;
  noPost: boolean;
  creditLimit?: number | null;
  postingControlReason?: string | null;
  postingControlAt?: string | null;
  postingControlByName?: string | null;
  taxCalculationEnabled: boolean;
  /** Late charges (slice 7c): checked out within billing.late_charge_days. */
  bookingStatusCode?: string | null;
  checkedOutOn?: string | null;
  lateChargeDays: number;
  canPostLate: boolean;
  lines: FolioLine[];
}

export interface PostFolioChargeRequest {
  chargeCode: string;
  description?: string | null;
  quantity: number;
  unitPrice: number;
  /** yyyy-MM-dd; omitted = today's business date. */
  chargeDate?: string | null;
  outletId?: string | null;
  /** The amount entered includes tax (slice 7a). Omitted = the property's "Prices include tax" setting. */
  amountIncludesTax?: boolean | null;
  // Folio Charge Voucher (slice 7c)
  /** Supplier bill / reference — required for a disbursement. */
  referenceNumber?: string | null;
  referenceDate?: string | null;
  /** Files upload (category folio_voucher) — required for a disbursement. */
  attachmentPath?: string | null;
  attachmentName?: string | null;
  /** Why — required for a recovery; printed on the voucher. */
  reason?: string | null;
  /** A disbursement: DESK_CASH or ACCOUNTS. */
  paidOutMode?: string | null;
  /** Generated once per dialog: a retry or double click posts once. */
  clientRef: string;
}

export interface FolioChargePreviewComponent {
  componentCode: string;
  componentName?: string | null;
  ratePercentage: number;
  taxAmount: number;
}

export interface FolioChargePreview {
  /** Whether the amount was taken as including tax (the tick, else the property setting). */
  amountIncludesTax: boolean;
  chargeCode: string;
  chargeName?: string | null;
  chargeDate: string;
  net: number;
  tax: number;
  total: number;
  taxCalculated: boolean;
  placeOfSupplyCode?: string | null;
  supplyNature?: string | null;
  components: FolioChargePreviewComponent[];
  payerType: string;
  payerName?: string | null;
  chargeNature: string;
  /** Posted after checkout: reopens the folio, billed on a supplementary invoice. */
  isLateCharge: boolean;
}

/** Why the folio refused (FolioPostingRefusal) — the desk acts differently on each. */
export type FolioRefusal =
  | 'None' | 'NotInHouse' | 'PostingDisabled' | 'CreditLimit'
  | 'FolioClosed' | 'FolioInvoiced' | 'NotReversible' | 'InvalidChargeType';

export interface FolioPostingResult {
  success: boolean;
  postingRef?: string | null;
  refusal: FolioRefusal;
  message?: string | null;
  wasAlreadyPosted: boolean;
  balanceAfter?: number | null;
  /** The Folio Charge Voucher of a desk posting (slice 7c). */
  voucherId?: string | null;
  voucherNumber?: string | null;
}

export interface FolioPostingControlRequest {
  noPost: boolean;
  creditLimit?: number | null;
  reason: string;
}

/** Folio line source as the desk reads it. */
export const folioSourceLabel = (l: Pick<FolioLine, 'kind' | 'sourceModule' | 'outletName' | 'paymentType'>): string => {
  if (l.kind === 'PAYMENT') return l.paymentType === 'Transfer' ? 'Advance' : (l.paymentType || 'Payment');
  switch ((l.sourceModule || '').toUpperCase()) {
    case 'FRONT_OFFICE': return 'Front Office';
    case 'DINEOPS': return l.outletName ? `Restaurant · ${l.outletName}` : 'Restaurant';
    case 'NIGHT_AUDIT': return 'Night audit';
    case '': return 'CRS';
    default: return l.outletName || (l.sourceModule as string);
  }
};
